import React, { useCallback, useEffect, useState } from "react";
import styles from "../../../styles/components/PaymentMethodsSection.module.css";
import AddPaymentMethodModal from "./AddPaymentMethodModal";
import {
  createSetupIntent,
  detachPaymentMethod,
  getPaymentMethods,
  setDefaultPaymentMethod,
} from "../../../services/billing.service";

const BRAND_LABELS = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "American Express",
  discover: "Discover",
  diners: "Diners Club",
  jcb: "JCB",
  unionpay: "UnionPay",
};

const PaymentMethodsSection = ({ accountId }) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [setupClientSecret, setSetupClientSecret] = useState("");
  const [setupLoading, setSetupLoading] = useState(false);
  const [actionError, setActionError] = useState("");
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const fetchPaymentMethods = useCallback(async () => {
    if (!accountId) return;
    try {
      setLoading(true);
      setLoadError("");
      const data = await getPaymentMethods(accountId);
      setPaymentMethods(Array.isArray(data) ? data : []);
    } catch (err) {
      setLoadError("No se pudo cargar los métodos de pago.");
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    fetchPaymentMethods();
  }, [fetchPaymentMethods]);

  const handleAddCard = async () => {
    try {
      setSetupLoading(true);
      setActionError("");
      const { client_secret } = await createSetupIntent(accountId);
      setSetupClientSecret(client_secret);
      setAddModalOpen(true);
    } catch (err) {
      setActionError(err.message || "No se pudo iniciar el formulario de tarjeta.");
    } finally {
      setSetupLoading(false);
    }
  };

  const handleSetDefault = async (paymentMethodId) => {
    try {
      setActionLoadingId(paymentMethodId);
      setActionError("");
      const updatedList = await setDefaultPaymentMethod(accountId, paymentMethodId);
      setPaymentMethods(Array.isArray(updatedList) ? updatedList : []);
    } catch (err) {
      setActionError(err.message || "No se pudo cambiar el método predeterminado.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDetach = async (paymentMethodId) => {
    try {
      setActionLoadingId(paymentMethodId);
      setActionError("");
      const updatedList = await detachPaymentMethod(accountId, paymentMethodId);
      setPaymentMethods(Array.isArray(updatedList) ? updatedList : []);
    } catch (err) {
      setActionError(err.message || "No se pudo eliminar el método de pago.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleAddDone = () => {
    setAddModalOpen(false);
    setSetupClientSecret("");
    fetchPaymentMethods();
  };

  const handleAddClose = () => {
    setAddModalOpen(false);
    setSetupClientSecret("");
  };

  return (
    <div className={styles.paymentMethodsBlock}>
      <div className={styles.paymentMethodsHeader}>
        <span className={styles.paymentMethodsTitle}>Métodos de pago</span>
        <button
          type="button"
          className={styles.addCardButton}
          onClick={handleAddCard}
          disabled={setupLoading}
        >
          {setupLoading ? "Iniciando..." : "+ Agregar tarjeta"}
        </button>
      </div>

      {loading && <div className={styles.stateBox}>Cargando...</div>}

      {!loading && loadError && <div className={styles.stateBoxError}>{loadError}</div>}

      {!loading && !loadError && paymentMethods.length === 0 && (
        <div className={styles.emptyState}>Sin métodos de pago registrados.</div>
      )}

      {!loading && !loadError && paymentMethods.length > 0 && (
        <div className={styles.cardsList}>
          {paymentMethods.map((paymentMethod) => {
            const isActing = actionLoadingId === paymentMethod.id;
            const expiryMonth = String(paymentMethod.exp_month).padStart(2, "0");
            const brandLabel = BRAND_LABELS[paymentMethod.brand] || paymentMethod.brand || "Tarjeta";

            return (
              <div key={paymentMethod.id} className={styles.cardRow}>
                <div className={styles.cardInfo}>
                  <span className={styles.cardBrand}>{brandLabel}</span>
                  <span className={styles.cardNumber}>•••• {paymentMethod.last4}</span>
                  <span className={styles.cardExpiry}>
                    {expiryMonth}/{paymentMethod.exp_year}
                  </span>
                  {paymentMethod.is_default && (
                    <span className={styles.defaultBadge}>Predeterminada</span>
                  )}
                </div>
                <div className={styles.cardActions}>
                  {!paymentMethod.is_default && (
                    <button
                      type="button"
                      className={styles.setDefaultButton}
                      onClick={() => handleSetDefault(paymentMethod.id)}
                      disabled={isActing}
                    >
                      Hacer predeterminada
                    </button>
                  )}
                  <button
                    type="button"
                    className={styles.detachButton}
                    onClick={() => handleDetach(paymentMethod.id)}
                    disabled={isActing}
                  >
                    {isActing ? "..." : "Eliminar"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {actionError && <div className={styles.actionError}>{actionError}</div>}

      <AddPaymentMethodModal
        open={addModalOpen}
        clientSecret={setupClientSecret}
        onClose={handleAddClose}
        onDone={handleAddDone}
      />
    </div>
  );
};

export default PaymentMethodsSection;
